
import { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { TestimonialCard } from './testimonial-card';
import React from 'react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';

const testimonialData = [
  {
    id: '1',
    quote: "I've used other services before, but Uber E-Visa was the smoothest. Loved the WhatsApp support — very responsive!",
    name: "Lyle Omana",
    location: "Philippines",
  },
  {
    id: '2',
    quote: "The visa process was incredibly smooth and fast. I had my visa in just 24 hours, exactly as promised. The team was very professional and helpful. Highly recommended for anyone needing a UAE visa!",
    name: "Sarah L.",
    location: "London, UK",
  },
  {
    id: '3',
    quote: "I was worried about the complex visa requirements, but Uber E-Visa made it so simple. Their AI checklist was a lifesaver, and the cost calculator was very accurate. Excellent service from start to finish.",
    name: "Ahmed M.",
    location: "Riyadh, KSA",
  },
  {
    id: '4',
    quote: "Fast, honest, and reliable - they live up to their promise. Tracking my application was super easy, and I was always kept informed about the status. I will definitely use their service again.",
    name: "John D.",
    location: "Sydney, Australia",
  }
];

const options: EmblaOptionsType = {
  loop: true,
  align: 'center',
};

const DotButton = ({ selected, onClick }: { selected: boolean; onClick: () => void }) => (
  <button
    className={cn(
      "w-3 h-3 rounded-full mx-1 transition-all duration-300",
      selected ? "bg-primary w-6" : "bg-gray-300"
    )}
    onClick={onClick}
    type="button"
  />
);

export function Testimonials() {
  const autoplay = useRef(
    Autoplay({ delay: 5000, stopOnInteraction: true })
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(options, [autoplay.current]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="position-relative bg-white py-5 px-3 border-top border-bottom">

      {/* Decorative quote */}
      <div
        className="position-absolute top-50 start-50 translate-middle text-muted opacity-25"
        style={{ fontSize: "25rem", lineHeight: 1, pointerEvents: "none" }}
        aria-hidden="true"
      >
        “
      </div>

      {/* Embla viewport */}
{/* Embla viewport */}
<div className="overflow-hidden" ref={emblaRef}>
  <div className="d-flex flex-nowrap">
    {testimonialData.map((testimonial) => (
      <div
        key={testimonial.id}
        className="flex-shrink-0 w-100"
      >
        <TestimonialCard testimonial={testimonial} />
      </div>
    ))}
  </div>
</div>


      {/* Prev */}
      <button
        onClick={scrollPrev}
        className="btn btn-light position-absolute top-50 start-0 translate-middle-y ms-3 rounded-circle shadow"
        style={{ width: 48, height: 48 }}
      >
        <ArrowLeft />
      </button>

      {/* Next */}
      <button
        onClick={scrollNext}
        className="btn btn-light position-absolute top-50 end-0 translate-middle-y me-3 rounded-circle shadow"
        style={{ width: 48, height: 48 }}
      >
        <ArrowRight />
      </button>

      {/* Dots */}
      <div className="d-flex justify-content-center mt-4">
        {testimonialData.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={`mx-1 rounded-pill border-0 ${
              index === selectedIndex ? "bg-primary" : "bg-secondary"
            }`}
            style={{
              width: index === selectedIndex ? 24 : 10,
              height: 10,
              transition: "all 0.3s ease",
            }}
          />
        ))}
      </div>
    </div>
  );
}
